/**
 * Testing Session Service
 *
 * API client for interactive testing session operations.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export interface CreateTestingSessionRequest {
  url: string;
  mode?: 'browser' | 'api' | 'full' | 'mobile';
  browser?: string;
  auto_execute?: boolean;
  max_pages?: number;
  max_depth?: number;
  test_name?: string;
  framework?: 'playwright' | 'selenium';
  tms_test_case_id?: string;
  project_id?: string;
  // Mobile testing fields
  platform?: 'linux' | 'android' | 'ios';
  device_name?: string;
  android_version?: string;
  ios_version?: string;
  app_url?: string;
  app_bundle_id?: string;
  // Pre-defined test credentials
  test_email?: string;
  test_phone?: string;
  test_password?: string;
  test_fullname?: string;
  otp_provider?: 'manual' | 'fixed' | 'email_imap' | 'api_endpoint' | 'totp' | 'android_sms' | 'mailinator';
  extra_credentials?: Record<string, string>;
  // OTP provider-specific settings
  otp_fixed_code?: string;
  otp_imap_host?: string;
  otp_imap_email?: string;
  otp_imap_password?: string;
  otp_api_url?: string;
  otp_api_headers?: Record<string, string>;
  otp_totp_secret?: string;
  otp_android_device?: string;
  persist_login_cookies?: boolean;
  // Intelligent testing context
  app_description?: string;
  testing_goals?: string[];
  key_user_journeys?: string[];
  business_rules?: string[];
  skip_context_gathering?: boolean;
  // Iterate mode: continue from a prior session
  parent_session_id?: string;
  // Per-page iterative testing mode
  page_iterative_mode?: boolean;
  page_review_mode?: boolean;
}

export interface GitPushRequest {
  provider: 'github' | 'gitlab';
  repo_url: string;
  branch: string;
  access_token: string;
  base_path?: string;
  commit_message?: string;
}

export interface GitPushResponse {
  success: boolean;
  commit_sha?: string;
  commit_url?: string;
  error?: string;
}

export interface CreateTestingSessionResponse {
  session_id: string;
  stream_url: string;
  status_url: string;
}

export interface TestingSessionStatus {
  session_id: string;
  phase: string;
  config: {
    url: string;
    mode: string;
    browser: string;
  };
  steps_count: number;
  results_count: number;
  passed: number;
  failed: number;
  pages_visited: string[];
  is_active: boolean;
}

export interface TestingSessionArtifacts {
  video_url?: string;
  har_url?: string;
  screenshots: string[];
  generated_code?: {
    files: Array<{ path: string; content: string; language: string }>;
    summary: string;
  };
  tms_entities: Array<{
    type: string;
    id: number;
    name: string;
  }>;
}

async function createSession(
  config: CreateTestingSessionRequest
): Promise<CreateTestingSessionResponse> {
  const response = await fetch(`${API_BASE}/testing/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to create testing session');
  }
  return response.json();
}

async function approveSteps(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/approve`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to approve steps');
  }
}

async function rejectSteps(sessionId: string, feedback?: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ feedback }),
  });
  if (!response.ok) {
    throw new Error('Failed to reject steps');
  }
}

async function submitCredentials(
  sessionId: string,
  username: string,
  password: string,
  extraFields?: Record<string, string>
): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/credentials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password, extra_fields: extraFields }),
  });
  if (!response.ok) {
    throw new Error('Failed to submit credentials');
  }
}

async function skipLogin(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/skip-login`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to skip login');
  }
}

async function solveCaptcha(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/solve-captcha`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to signal CAPTCHA solved');
  }
}

async function skipCaptcha(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/skip-captcha`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to skip CAPTCHA');
  }
}

async function approveReexecution(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/approve-reexecution`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to approve re-execution');
  }
}

async function skipReexecution(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/skip-reexecution`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to skip re-execution');
  }
}

async function endSession(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/end`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to end session');
  }
}

async function cancelSession(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/cancel`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to cancel session');
  }
}

async function addStep(
  sessionId: string,
  step: { action_id: string; parameters: Record<string, any>; description: string }
): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/step`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(step),
  });
  if (!response.ok) {
    throw new Error('Failed to add step');
  }
}

async function getStatus(sessionId: string): Promise<TestingSessionStatus> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/status`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to get session status');
  }
  return response.json();
}

async function getArtifacts(sessionId: string): Promise<TestingSessionArtifacts> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/artifacts`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to get session artifacts');
  }
  return response.json();
}

function getStreamUrl(sessionId: string): string {
  return `${API_BASE}/testing/sessions/${sessionId}/stream`;
}

async function getLiveScreenshot(sessionId: string): Promise<string | null> {
  try {
    const response = await fetch(
      `${API_BASE}/testing/sessions/${sessionId}/live-screenshot`,
      { credentials: 'include' }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.screenshot_b64 || null;
  } catch {
    return null;
  }
}

export interface SessionChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SessionChatStreamCallbacks {
  onToken: (token: string) => void;
  onComplete: () => void;
  onError: (error: string) => void;
  onCommandQueued?: (command: string) => void;
}

async function sessionChatStream(
  sessionId: string,
  message: string,
  history: SessionChatMessage[],
  callbacks: SessionChatStreamCallbacks
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/chat/stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message, history }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    callbacks.onError(error.detail || 'Failed to start session chat');
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError('No response body');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        try {
          const event = JSON.parse(data);
          if (event.type === 'token') {
            callbacks.onToken(event.content);
          } else if (event.type === 'command_queued') {
            callbacks.onCommandQueued?.(event.content);
          } else if (event.type === 'complete') {
            callbacks.onComplete();
            return;
          } else if (event.type === 'error') {
            callbacks.onError(event.content || 'Stream error');
            return;
          }
        } catch (e) {
          console.error('Failed to parse session chat SSE data:', e);
        }
      }
    }
  }

  // Stream ended without explicit complete event
  callbacks.onComplete();
}

async function submitContext(
  sessionId: string,
  context: {
    app_description?: string;
    testing_goals?: string[];
    key_user_journeys?: string[];
    business_rules?: string[];
  }
): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(context),
  });
  if (!response.ok) {
    throw new Error('Failed to submit testing context');
  }
}

async function skipContext(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/skip-context`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to skip context gathering');
  }
}

async function pushToGit(
  sessionId: string,
  config: GitPushRequest
): Promise<GitPushResponse> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/push-to-git`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(config),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to push to Git');
  }
  return response.json();
}

async function uploadApk(sessionId: string, file: File): Promise<{ success: boolean; app_package?: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/upload-apk`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to upload APK');
  }
  return response.json();
}

export interface RegenerateCodeResponse {
  code: {
    files: Array<{ path: string; content: string; language: string; framework?: string }>;
    summary: string;
    framework: string;
    tms_test_case_id?: string;
    user_feedback?: string;
  };
}

export interface ExecuteCodeResult {
  name: string;
  status: 'passed' | 'failed' | 'error' | 'skipped';
  error: string | null;
  duration: number;
}

export interface ExecuteCodeResponse {
  total: number;
  passed: number;
  failed: number;
  errors: number;
  results: ExecuteCodeResult[];
  output: string;
  success: boolean;
}

async function updateSessionCode(
  sessionId: string,
  code: Record<string, any>
): Promise<{ status: string }> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/code`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update code');
  }
  return response.json();
}

async function regenerateCode(
  sessionId: string,
  feedback: string
): Promise<RegenerateCodeResponse> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/regenerate-code`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ feedback }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to regenerate code');
  }
  return response.json();
}

async function browserInput(
  sessionId: string,
  action: string,
  params: { x?: number; y?: number; text?: string; delta_x?: number; delta_y?: number }
): Promise<Record<string, any>> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/browser-input`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action, ...params }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to send browser input');
  }
  return response.json();
}

async function executeCode(
  sessionId: string,
  code?: Record<string, any>
): Promise<ExecuteCodeResponse> {
  const response = await fetch(
    `${API_BASE}/testing/sessions/${sessionId}/execute-code`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code: code || null }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to execute code');
  }
  return response.json();
}

async function submitApiConfig(
  sessionId: string,
  config: {
    auth_email: string;
    auth_password: string;
    auth_endpoint?: string;
    token_path?: string;
    selected_tags?: string[];
    max_tests?: number;
    docs_url?: string;
  }
): Promise<void> {
  return submitCredentials(sessionId, config.auth_email, config.auth_password, {
    auth_endpoint: config.auth_endpoint || '',
    token_path: config.token_path || 'access_token',
    selected_tags: JSON.stringify(config.selected_tags || []),
    max_tests: String(config.max_tests || 50),
    docs_url: config.docs_url || '',
  });
}

export interface PriorSessionSummary {
  id: string;
  config: { url: string; mode: string };
  total_steps: number;
  passed_count: number;
  failed_count: number;
  created_at: string;
  iteration_number: number;
}

async function findPriorSessions(
  url: string,
  mode: string = 'browser'
): Promise<{ sessions: PriorSessionSummary[] }> {
  const params = new URLSearchParams({ url, mode });
  const response = await fetch(`${API_BASE}/testing/sessions/prior?${params}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to find prior sessions');
  }
  return response.json();
}

async function approveTestCases(
  sessionId: string,
  modifiedTestCases?: Array<{ name: string; description: string; priority: string; category: string }>
): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/approve-test-cases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ modified_test_cases: modifiedTestCases || null }),
  });
  if (!response.ok) {
    throw new Error('Failed to approve test cases');
  }
}

async function approveCode(
  sessionId: string,
  modifiedCode?: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/approve-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ modified_code: modifiedCode || null }),
  });
  if (!response.ok) {
    throw new Error('Failed to approve code');
  }
}

async function selectPages(sessionId: string, nodeIds: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/select-pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ node_ids: nodeIds }),
  });
  if (!response.ok) {
    throw new Error('Failed to submit page selection');
  }
}

async function selectAction(sessionId: string, action: string): Promise<void> {
  const response = await fetch(`${API_BASE}/testing/sessions/${sessionId}/select-action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ action }),
  });
  if (!response.ok) {
    throw new Error('Failed to select testing action');
  }
}

const testingService = {
  createSession,
  approveSteps,
  rejectSteps,
  submitCredentials,
  submitApiConfig,
  skipLogin,
  submitContext,
  skipContext,
  solveCaptcha,
  skipCaptcha,
  approveReexecution,
  skipReexecution,
  approveTestCases,
  approveCode,
  selectPages,
  selectAction,
  endSession,
  cancelSession,
  addStep,
  getStatus,
  getArtifacts,
  getStreamUrl,
  getLiveScreenshot,
  sessionChatStream,
  pushToGit,
  uploadApk,
  updateSessionCode,
  regenerateCode,
  executeCode,
  browserInput,
  findPriorSessions,
};

export default testingService;

// ============================================================================
// Codebase Analysis API
// ============================================================================

export async function analyzeCodebase(request: {
  project_id: string;
  organization_id: string;
  source_type: 'github' | 'gitlab' | 'local';
  repo_url?: string;
  local_path?: string;
  branch?: string;
  access_token?: string;
}): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function listCodebaseSnapshots(
  projectId: string,
  organizationId: string,
): Promise<any[]> {
  const resp = await fetch(
    `${API_BASE}/codebase/snapshots/${projectId}?organization_id=${organizationId}`,
    { credentials: 'include' },
  );
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function getCodebaseSnapshot(snapshotId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/snapshot/${snapshotId}`, {
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function runExistingTests(request: {
  snapshot_id: string;
  test_paths?: string[];
  timeout?: number;
}): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/run-existing-tests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function generateCodebaseTests(request: {
  snapshot_id: string;
  project_id: string;
  organization_id: string;
  test_levels?: string[];
  target_files?: string[];
  focus_areas?: string[];
  framework?: string;
}): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/generate-tests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function listGeneratedAssets(
  projectId: string,
  organizationId: string,
  testLevel?: string,
): Promise<any[]> {
  let url = `${API_BASE}/codebase/assets/${projectId}?organization_id=${organizationId}`;
  if (testLevel) url += `&test_level=${testLevel}`;
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function getGeneratedAsset(assetId: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/asset/${assetId}`, {
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function getAssetVersions(assetId: string): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/codebase/asset/${assetId}/versions`, {
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function pushAssetsToRepo(request: {
  project_id: string;
  organization_id: string;
  asset_ids: string[];
  branch?: string;
  commit_message?: string;
  base_path?: string;
}): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/assets/push`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function pullAssetsFromRepo(request: {
  project_id: string;
  organization_id: string;
  file_paths: string[];
}): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/assets/pull`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function updateAssetContent(
  assetId: string,
  content: string,
  changeDescription?: string,
): Promise<any> {
  const resp = await fetch(`${API_BASE}/codebase/asset/${assetId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ content, change_description: changeDescription || '' }),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function generateCIConfig(
  projectId: string,
  organizationId: string,
  ciProvider: string = 'github_actions',
): Promise<Record<string, string>> {
  const resp = await fetch(
    `${API_BASE}/codebase/ci-config/${projectId}?organization_id=${organizationId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ci_provider: ciProvider }),
    },
  );
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

/**
 * List supported TMS reporter plugins by framework/language.
 */
export async function listSupportedPlugins(): Promise<{
  frameworks: Array<{
    framework: string;
    language: string;
    plugin_package: string | null;
    post_execution_only: boolean;
    supports_env_vars: boolean;
  }>;
  description: string;
}> {
  const resp = await fetch(`${API_BASE}/codebase/plugins/supported`, {
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}
